/**
 * A theme change must not be animated. Not quickly — not at all.
 *
 * THE REASON IS GEOMETRIC, NOT AESTHETIC. Light and dark are an INVERSION:
 * near-white text on a near-black surface becomes near-black text on a
 * near-white surface. Interpolating both ends of that pair over the same 180ms
 * makes them cross, and at the crossing the contrast ratio is 1:1 — the text is
 * the colour of what it sits on. Measured on this design system's own
 * stylesheets, `.btn` and `.tag` fall from 14.54:1 to 1.02:1 and `.btn-primary`
 * to 1.5:1 at the instant of the flip.
 *
 * It is worse than a symmetric crossing, because the two halves are not even
 * transitioned together: `body` carries no transition, so the SURFACE snaps to
 * the new theme while the TEXT eases across from the old one. The band
 * therefore STARTS at its worst — maximum inversion, 1.02:1 — and improves from
 * there over 180ms. Real frames land inside it: sampled runs caught 2.5:1 and
 * 2.65:1, and `.btn-primary` dipped to 4.48:1, under WCAG AA's 4.5:1, while
 * painting.
 *
 * No amount of tuning fixes that. Shortening the duration shortens the band;
 * transitioning the surface too makes the pair cross in the middle instead of
 * starting at the bottom. The only state with no band is NO TRANSITION, so this
 * turns transitions off for exactly as long as the theme is changing, and
 * leaves hover and focus transitions — which never invert — alone.
 */

/**
 * Present on <html> only while a theme change is being applied. The stylesheet
 * turns every transition off under it.
 *
 * It is an ATTRIBUTE rather than a class because `data-theme` already is, and
 * because a class on <html> is the thing app code is most likely to clobber
 * with `className =`.
 */
export const THEME_SWITCH_ATTR = 'data-theme-switching';

/**
 * Force the pending style recalculation to happen NOW.
 *
 * This is the part that must not depend on a frame. A duration of "almost zero"
 * still creates a transition and still needs a sample before it advances, so
 * anything that reads or paints before that sample — a synchronous audit, a
 * screenshot, a print, a compositor on another thread — sees the START of the
 * fade. Reading a layout-or-style-dependent property is synchronous: after it
 * returns, the declaration above is in effect.
 *
 * Guarded because this module is exercised against document stand-ins that have
 * no defaultView, and because a detached root has no view at all. When there is
 * nothing to flush there is also no rendering to protect.
 */
function flushStyle(root: Element): void {
  try {
    root.ownerDocument?.defaultView?.getComputedStyle(root).getPropertyValue('color');
  } catch {
    /* No view, no paint to protect. */
  }
}

/**
 * Apply a theme change with transitions suppressed around it.
 *
 * The sequence matters and every step is synchronous:
 *   1. mark the root, and flush — so "no transitions" is the rule in force;
 *   2. change the theme, and flush — so the new colours are COMMITTED while
 *      that rule still holds, which is what stops a transition being created;
 *   3. unmark, and flush — so the next hover is animated again.
 *
 * Step 4 is the belt to that braces: a theme change driven by the OPERATING
 * SYSTEM is not routed through here at all — the media query re-evaluates and
 * the browser may have created the transitions before any listener of ours
 * runs. Finishing whatever is running jumps it to its end state, which is the
 * same picture the suppression would have produced. It is deliberately not the
 * primary mechanism: cancelling after the fact still allows one frame of band
 * on a slow event loop, whereas suppressing before the commit allows none.
 */
export function withThemeTransitionSuppressed(root: Element, change: () => void): void {
  root.setAttribute(THEME_SWITCH_ATTR, '');
  flushStyle(root);

  try {
    change();
    flushStyle(root);
    finishRunningTransitions(root.ownerDocument);
  } finally {
    /* The `finally` is the whole point. This attribute kills EVERY transition
       in the document while it is present, so a throw inside `change` — a
       storage error, a stale root, anything — would leave the interface with no
       animation at all until the next reload, and nothing on screen would say
       why. Suppression must be impossible to leave switched on. */
    root.removeAttribute(THEME_SWITCH_ATTR);
    flushStyle(root);
  }
}

/**
 * End EVERY CSS transition that is already running — not only the colour ones.
 *
 * The heading says "every" because the loop does not discriminate by property,
 * and the argument below has to carry that rather than quietly assume colour.
 *
 * `finish()` rather than `cancel()`: cancelling reverts to the PRE-transition
 * value, which during a theme change is the OLD theme's colour — the band,
 * held indefinitely. Finishing lands on the value the element's current styles
 * ask for, which after the theme change is the new theme's.
 *
 * THAT REASONING IS COLOUR-SPECIFIC, SO HERE IS WHY IT IS SAFE FOR THE REST. A
 * transition in flight during a theme change is almost always an interaction
 * effect — a hover lift, a focus ring easing in — whose END state is the one
 * the element should hold for as long as the pointer or focus stays where it
 * is. Finishing it early is visible only as a shorter animation, and the
 * element is left in the state its own selectors already specify. If a
 * transition is ever added whose end state is NOT the correct resting state,
 * this becomes wrong for that property and the fix is to filter by
 * `transitionProperty` here — that is the falsifiable version of the claim.
 *
 * Only CSSTransition is touched. A keyframe animation is an entrance effect
 * with a beginning and an end an author chose, and finishing those on a theme
 * change would skip content into view.
 */
export function finishRunningTransitions(doc: Document | null | undefined): number {
  const all = doc?.getAnimations?.();
  if (!all) return 0;
  let n = 0;
  for (const a of all) {
    if (a.constructor.name !== 'CSSTransition') continue;
    try {
      a.finish();
      n += 1;
    } catch {
      /* A transition that has already finished throws; nothing to do. */
    }
  }
  return n;
}

/**
 * The OS-driven half of the same defect.
 *
 * `system` is the DEFAULT choice, and on that setting nobody clicks anything:
 * the user flips their OS to light, the media query re-evaluates, and every
 * transitioned element cross-fades with no JavaScript of ours involved. A fix
 * that only covered the toggle would leave the band in place for the setting
 * most people are on.
 *
 * Returns an unsubscribe function. Safe to call where matchMedia does not
 * exist — the server, a test environment — in which case it does nothing and
 * unsubscribing is also nothing.
 */
export function watchSystemThemeChange(root: Element): () => void {
  let mql: MediaQueryList | undefined;
  try {
    mql = root.ownerDocument?.defaultView?.matchMedia('(prefers-color-scheme: light)');
  } catch {
    return () => {};
  }
  if (!mql?.addEventListener) return () => {};

  const onChange = () => {
    // The change has already happened; there is nothing to bracket. Suppress
    // anyway so that anything still being committed is committed without a
    // transition, then finish what the browser already started.
    withThemeTransitionSuppressed(root, () => {});
  };

  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}
